// Herd partners: two accounts that keep one herd between them. Every horse
// either of them owns is shared with the other, now and as they are added.
//
// It is a partnership rather than a one-way grant because it runs both ways —
// and that is exactly why the other person has to accept. Your horses are
// shared the moment you invite (that is your decision to make); theirs join
// only once they say yes, or anyone could invite you and walk into your herd.
//
// Client horses stay out of it. A horse whose creator is also its trainer is
// client work — billed, not owned in the ranch sense — and belongs to the
// provider, not the household.
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  arrayUnion,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';

const partnersRef = collection(db, 'herdPartners');

const person = (u) => ({ email: u?.email || '', name: u?.name || u?.displayName || u?.email || '' });

// One document per pair. `appliedBy` records whose horses have been shared so
// far; `cleanedBy` records who has un-shared theirs after it ends. Both are
// per-device work, since only an owner can change who is on their horses.
export const inviteHerdPartner = (me, them, permission, includeClients = false) =>
  addDoc(partnersRef, {
    uids: [me.uid, them.uid],
    people: { [me.uid]: person(me), [them.uid]: person(them) },
    invitedBy: me.uid,
    permission: permission === 'edit' ? 'edit' : 'view',
    // Some partners are partners in everything, client horses included.
    includeClients: !!includeClients,
    status: 'pending',
    appliedBy: [],
    cleanedBy: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

export const acceptHerdPartnership = (id) =>
  updateDoc(doc(partnersRef, id), { status: 'active', updatedAt: serverTimestamp() });

// Declining a pending invite simply removes it — nothing was ever shared from
// the declining side, and the inviter's sync takes their horses back.
export const declineHerdPartnership = (id) => deleteDoc(doc(partnersRef, id));

export const endHerdPartnership = (id, byUid) =>
  updateDoc(doc(partnersRef, id), { status: 'ended', endedBy: byUid, updatedAt: serverTimestamp() });

export const markPartnershipApplied = (id, uid) =>
  updateDoc(doc(partnersRef, id), { appliedBy: arrayUnion(uid), updatedAt: serverTimestamp() });

export const markPartnershipCleaned = (id, uid) =>
  updateDoc(doc(partnersRef, id), { cleanedBy: arrayUnion(uid), updatedAt: serverTimestamp() });

export const removePartnership = (id) => deleteDoc(doc(partnersRef, id));

export const subscribeToHerdPartnerships = (uid, callback) => {
  if (!uid) { callback([]); return () => {}; }
  const q = query(partnersRef, where('uids', 'array-contains', uid));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, () => { /* keep last-known state on transient error */ });
};

// The other person in a partnership, as a share target.
export const partnerOf = (p, uid) => {
  const other = (p.uids || []).find((u) => u !== uid);
  if (!other) return null;
  return { uid: other, ...(p.people?.[other] || {}), permission: p.permission || 'view', includeClients: !!p.includeClients };
};

export const activePartners = (partnerships, uid) =>
  (partnerships || []).filter((p) => p.status === 'active').map((p) => partnerOf(p, uid)).filter(Boolean);

// Fetched once when a horse is created, so the new horse starts out shared.
// Filtered here rather than in the query: array-contains plus an equality on
// another field needs a composite index, and there will only ever be a few.
export const fetchActivePartners = async (uid) => {
  if (!uid) return [];
  const snap = await getDocs(query(partnersRef, where('uids', 'array-contains', uid)));
  return activePartners(snap.docs.map((d) => d.data()), uid);
};

// The horses a partnership covers: the ones this user owns outright. A horse
// where they are the trainer is a client horse and is left alone; one that was
// merely shared to them is not theirs to share on.
export const isOwnHerdHorse = (horse, uid) =>
  !!uid && horse?.ownerId === uid && !(horse?.trainers || []).some((t) => t?.uid === uid);

// Whether a given partnership reaches a given horse of mine.
export const partnershipCovers = (horse, uid, includeClients) =>
  includeClients ? (!!uid && horse?.ownerId === uid) : isOwnHerdHorse(horse, uid);
